import { Provider } from '@vgtv/api-client';
import { ConsentManager } from '@vgtv/consent-manager';

import { lowercase } from '../../utils/string';
import { getDeviceType } from '../../utils/device';
import { getAdPermission } from '../../config/adn/getAdPermission';
import { getTcfConsentKeywords } from '../../config/adn/getTcfConsentKeywords';

import { ChromecastController } from './chromecastController';
import { ChromecastSessionManager } from './chromecastSessionManager';

const PROVIDER_NAME = 'chromecast' as const;
const DEFAULT_MIN_DVR_WINDOW = 30;

const createVmapUrl = async (
  vendor: Provider,
  id: number,
  config: {
    site?: string;
    externalUid?: string;
    consentManager?: ConsentManager;
    skippable?: boolean;
  }
) => {
  const params = new URLSearchParams({
    supplyType: `web_${getDeviceType()}`,
    sdkType: 'ima',
    ...(await getTcfConsentKeywords()),
  });

  if (config.site) {
    params.set('site', config.site);
  }
  if (config.externalUid) {
    params.set('externalUid', config.externalUid);
  }

  if (config.consentManager) {
    params.set(
      'aa-sch-cmp_advertising',
      await getAdPermission(config.consentManager)
    );
  }

  if (config.skippable) {
    params.set('skippable', 'true');
  }

  return `https://vmap.stream.schibsted.media/${vendor}/${id}?${params}`;
};

export const initChromecastProvider = ({
  player,
  vendor,
  site,
  externalUid,
  consentManager,
}: {
  player: jwplayer.JWPlayer;
  vendor: Provider;
  site?: string;
  externalUid?: string;
  consentManager?: ConsentManager;
  getUserAccesses?: () => Promise<string[]>;
}) => {
  const chromecast = new ChromecastController(new ChromecastSessionManager());

  interface ChromecastProvider {
    // these methods are added in the constructor
    on: jwplayer.Provider['on'];
    once: jwplayer.Provider['once'];
    off: jwplayer.Provider['off'];
    trigger: jwplayer.Provider['trigger'];
    // these 'DefaultProvider' methods are added to the prototype when provider is registered
    sendMediaType: (sources: jwplayer.PlaylistItemSource[]) => void;
    setState: (state: jwplayer.InternalPlayerState) => void;
  }

  class ChromecastProvider implements jwplayer.ImplementedProvider {
    private container: HTMLElement | null;

    name = PROVIDER_NAME;

    private playlistItem?: jwplayer.PlaylistItem;

    private readonly config: jwplayer.PlayerConfig;

    private readonly player: jwplayer.JWPlayer;

    private readonly chromecast: ChromecastController;

    private _breakClip?: chrome.cast.media.BreakClip;

    static supports() {
      return !!cast.framework.CastContext.getInstance().getCurrentSession();
    }

    static getName() {
      return {
        name: PROVIDER_NAME,
      };
    }

    getName() {
      return {
        name: PROVIDER_NAME,
      };
    }

    constructor(playerId: string, config: jwplayer.PlayerConfig) {
      const backboneEvents = jwplayer(playerId).Events;

      Object.assign(this, backboneEvents);

      this.container = null;
      this.config = config;
      this.player = player;
      this.chromecast = chromecast;

      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
        this.handleCurrentTimeChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED,
        this.handleIsMutedChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
        this.handleVolumeChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.DURATION_CHANGED,
        this.handleDurationChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
        this.handlePlayerStateChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
        this.handleIsConnectedChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.BREAK_CLIP_ID_CHANGED,
        this.handleBreakClipIdChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.CURRENT_BREAK_CLIP_TIME_CHANGED,
        this.handleCurrentBreakClipTimeChanged.bind(this)
      );
      this.chromecast.addEventListener(
        cast.framework.RemotePlayerEventType.CURRENT_BREAK_TIME_CHANGED,
        this.handleCurrentBreakClipTimeChanged.bind(this)
      );
    }

    public setContainer(element: HTMLElement): void {
      this.container = element;
    }

    public getContainer(): HTMLElement | null {
      return this.container;
    }

    public async init(playlistItem: jwplayer.PlaylistItem): Promise<void> {
      if (this.playlistItem === playlistItem) {
        return;
      }

      this._breakClip = undefined;
      this.playlistItem = playlistItem;

      this.setState(this.getInitialState());

      const handleMediaInfoChanged = async () => {
        this.extractEmbeddedCaptions();

        await this.setSubtitlesTrack(this.player.getCurrentCaptions());
      };

      try {
        await this.chromecast.load({
          playlistItem,
          startTime: playlistItem.starttime,
          vmapUrl:
            playlistItem.svp.settings?.na ||
            !playlistItem.svp.stream.attributes.showAds
              ? undefined
              : await createVmapUrl(vendor, playlistItem.id, {
                  site,
                  externalUid,
                  consentManager,
                  skippable: false,
                }),
        });

        // wait for chromecast to load media object and try to extract hls captions if such are present
        this.chromecast.addEventListener(
          cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
          handleMediaInfoChanged,
          { once: true }
        );
      } catch (err) {
        if (err instanceof chrome.cast.Error) {
          console.warn(err);

          return;
        }

        throw err;
      }
    }

    public async load(playlistItem: jwplayer.PlaylistItem): Promise<void> {
      await this.init(playlistItem);

      await this.play();
    }

    public play(): Promise<void> {
      this.chromecast.play();

      return Promise.resolve();
    }

    public pause(): void {
      this.chromecast.pause();
    }

    public mute(state?: boolean): void {
      this.chromecast.mute(state);
    }

    public volume(volume: number): void {
      this.chromecast.setVolume(volume / 100.0);
    }

    public seek(seekPos: number): void {
      const currentTime = this.chromecast.getCurrentTime();

      this.trigger('seek', {
        position: this.getPosition(),
        offset: seekPos,
        duration: this.getDuration(),
        currentTime,
        seekRange: this.getSeekRange(),
        metadata: {
          currentTime,
        },
      });

      let position = seekPos;

      if (position < 0) {
        position += this.getSeekRange().end;
      }

      this.chromecast.seek(position);
    }

    public getSeekRange() {
      const liveSeekableRange = this.chromecast.getLiveSeekableRange();

      if (liveSeekableRange) {
        return {
          start: liveSeekableRange.start!,
          end: liveSeekableRange.end!,
        };
      }

      return {
        start: 0,
        end: Math.max(this.chromecast.getDuration(), 0),
      };
    }

    public getDuration(): number {
      const duration = this.chromecast.getDuration();
      const liveSeekableRange = this.chromecast.getLiveSeekableRange();

      if (liveSeekableRange || duration < 0) {
        const seekRange = this.getSeekRange();
        const seekableDuration = seekRange.end - seekRange.start;

        return this.isDvr(seekableDuration) ? -seekableDuration : Infinity;
      }

      return duration;
    }

    public getPosition(): number {
      const currentTime = this.chromecast.getCurrentTime();
      const duration = this.getDuration();
      const liveSeekableRange = this.chromecast.getLiveSeekableRange();

      if (liveSeekableRange || duration < 0) {
        const seekRange = this.getSeekRange();
        const seekableDuration = seekRange.end - seekRange.start;

        if (this.isDvr(seekableDuration)) {
          return currentTime - seekRange.end;
        }
      }

      // due to some rounding errors when retrieving estimated time,
      // the current time value might be slightly bigger than a duration
      return Math.min(currentTime, duration);
    }

    async setSubtitlesTrack(trackIndex: number) {
      const captions = this.player.getCaptionsList()[trackIndex];

      if (!captions) {
        return false;
      }

      if (trackIndex > 0) {
        return this.chromecast.enableCaptions(captions.label);
      }

      return this.chromecast.disableCaptions();
    }

    async refreshSubtitlesTrack() {
      const trackIndex = this.player.getCurrentCaptions();

      await this.syncSubtitlesTrack(0);

      await this.syncSubtitlesTrack(trackIndex);
    }

    syncSubtitlesTrack(trackIndex: number) {
      return new Promise<void>((resolve, reject) => {
        this._syncSubtitlesTrack(trackIndex, resolve, reject);
      });
    }

    async _syncSubtitlesTrack(
      trackIndex: number,
      successCallback: () => void,
      errorCallback: (error: unknown) => void
    ) {
      try {
        const success = await this.setSubtitlesTrack(trackIndex);

        if (success) {
          successCallback();
        } else {
          this.chromecast.addEventListener(
            cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
            () => {
              this._syncSubtitlesTrack(
                trackIndex,
                successCallback,
                errorCallback
              );
            },
            { once: true }
          );
        }
      } catch (err) {
        errorCallback(err);
      }
    }

    public getQualityLevels(): jwplayer.Level[] {
      return [];
    }

    private isDvr(seekableDuration: number) {
      return (
        seekableDuration !== Infinity &&
        Math.abs(seekableDuration) >=
          Math.max(this.config.minDvrWindow!, DEFAULT_MIN_DVR_WINDOW)
      );
    }

    private getInitialState(): jwplayer.InternalPlayerState {
      const state = this.chromecast.getState();

      switch (state) {
        case chrome.cast.media.PlayerState.PLAYING:
          return 'playing';
        case chrome.cast.media.PlayerState.PAUSED:
          return 'paused';
        case chrome.cast.media.PlayerState.IDLE:
        case chrome.cast.media.PlayerState.BUFFERING:
        case null:
        default:
          return 'buffering';
      }
    }

    private triggerTimeEvent() {
      const currentTime = this.chromecast.getCurrentTime();

      this.trigger('time', {
        position: this.getPosition(),
        duration: this.getDuration(),
        currentTime,
        seekRange: this.getSeekRange(),
        metadata: {
          currentTime,
        },
      });
    }

    private extractEmbeddedCaptions() {
      const tracks = this.chromecast.getEmbeddedCaptions();

      if (tracks.length) {
        this.trigger('subtitlesTracks', {
          tracks: tracks.map((track) => {
            return {
              name: track.name,
              language: track.language,
              kind: 'subtitles' as const,
              cues: null,
            };
          }),
        });
      }
    }

    private handleBreakClipIdChanged() {
      const breakClip = this.chromecast.getCurrentBreakClip();

      if (breakClip) {
        this._breakClip = breakClip;

        this.trigger('castAdData', {
          meta: {
            clickThroughUrl: breakClip.clickThroughUrl!,
            duration: breakClip.duration!,
            sequence: breakClip.sequence,
            podcount: breakClip.podcount,
          },
        });
      } else if (this._breakClip) {
        this._breakClip = undefined;

        this.trigger('castAdSlotComplete');

        // chromecast active tracks go out of sync when ad is finished
        this.refreshSubtitlesTrack();
      }
    }

    private handleCurrentBreakClipTimeChanged() {
      const breakClip = this.chromecast.getCurrentBreakClip();

      if (!breakClip) {
        return;
      }

      this.trigger('castAdProgress', {
        position: this.getPosition(),
        duration: this.getDuration(),
      });
    }

    private handleCurrentTimeChanged() {
      this.triggerTimeEvent();
    }

    private handleDurationChanged() {
      this.triggerTimeEvent();
    }

    private handleIsMutedChanged() {
      if (this.player.getMute() !== this.chromecast.isMuted()) {
        this.player.setMute(this.chromecast.isMuted());
      }
    }

    private handleVolumeChanged() {
      const volume = this.chromecast.getVolume() * 100;

      if (Math.abs(this.player.getVolume() - volume) > 2) {
        this.player.setVolume(volume);
      }
    }

    private handlePlayerStateChanged() {
      const state = this.chromecast.getState();

      if (!state) {
        return;
      }

      if (this.chromecast.isComplete()) {
        this.trigger('complete');

        return;
      }

      if (
        state === chrome.cast.media.PlayerState.PLAYING ||
        state === chrome.cast.media.PlayerState.BUFFERING ||
        state === chrome.cast.media.PlayerState.PAUSED
      ) {
        this.setState(lowercase(state));
      }
    }

    private handleIsConnectedChanged() {
      this.player.trigger('cast', {
        available: this.chromecast.isAvailable(),
        active: this.chromecast.isConnected(),
        deviceName: this.chromecast.getDeviceName(),
        type: 'cast',
      });
    }
  }

  return ChromecastProvider;
};
